<template>
  <div class="d-flex flex-column">
    <div class="grid-header">
      <h1>{{ $t("operaciones_factura.titulo") }}</h1>
      <div class="toolbar">
      </div>
    </div>

    <DxDataGrid v-bind="gridProperties" :data-source="gridSource" ref="grid" class="dx-card wide-card grid-liquidaciones">
      <DxToolbar>
        <DxItem location="after" template="exportButtonTemplate" />
      </DxToolbar>
      <DxColumn :group-index="0" data-field="NombreTramite" group-cell-template="tramiteGroupTemplate" />
      <DxColumn data-field="CodigoSecuencial" :caption="$t('liquidaciones.grid.id')" format="0000000000"></DxColumn>
      <DxColumn data-field="TipoConcepto" :caption="$t('liquidaciones.grid.tipo_concepto')">
        <DxLookup column-auto-width="true" v-bind="getLookup('liquidaciones', 'tipo_concepto')"></DxLookup>
      </DxColumn>
      <DxColumn data-field="FechaActivacion" :caption="$t('liquidaciones.grid.fecha_activacion')" data-type="date" format="dd/MM/yyyy"></DxColumn>
      <DxColumn data-field="CodigoEstadoLiquidacion" :caption="$t('operaciones.grid.liquidado')" cell-template="liquidadoTemplate">
        <DxLookup v-bind="getLookup('operaciones', 'liquidado')" />
      </DxColumn>
      <DxColumn data-field="ComisionEfectiva" :caption="$t('operaciones.grid.comision')" cell-template="desgloseComisionTemplate" />
      <DxColumn data-field="RetrocomisionEfectiva" :caption="$t('operaciones.grid.retrocomision')"
        cell-template="desgloseRetrocomisionTemplate"
        :calculate-filter-expression="retrocomisionEfectivaFilter" />
      <DxColumn data-field="DocumentoTitular" :caption="$t('liquidaciones.grid.documento_cliente')"></DxColumn>
      <DxColumn data-field="DireccionTitular" :caption="$t('liquidaciones.grid.direccion_cliente')"></DxColumn>
      <DxColumn data-field="IdTipoSuministro" :caption="$t('operaciones.grid.suministro')">
        <DxLookup column-auto-width="true" v-bind="getLookup('rappels_mensuales', 'tipo_suministro')"></DxLookup>
      </DxColumn>
      <DxColumn data-field="Cups" :caption="$t('operaciones.grid.cups')"></DxColumn>

      <DxColumn type="buttons" :caption="$t('liquidaciones.grid.acciones')" :fixed="true" fixed-position="right" width="100px" :buttons="[
        {
          cssClass: 'btn btn-link text-decoration-none btn-sm',
          icon: 'fa-solid fa-magnifying-glass text-success',
          hint: $t('liquidaciones.grid.ver_detalle'),
          onClick: (e: any) => verDetalle(e)
        }
      ]"/>

      <DxSummary>
        <DxGroupItem :show-in-group-footer="false" :align-by-column="true" column="ComisionEfectiva" summary-type="sum" display-format="Total: {0}" :value-format="formatTotalComision"  >
        </DxGroupItem>
        <DxGroupItem :show-in-group-footer="false" :align-by-column="true" column="RetrocomisionEfectiva" summary-type="sum" display-format="Total: {0}" :value-format="formatTotalRetrocomision"  >
        </DxGroupItem>
      </DxSummary>

      <DxColumnFixing :enabled="true" />

      <template #tramiteGroupTemplate="{ data }">
        <span>
          {{ data.value }}
        </span>
      </template>
      <template #liquidadoTemplate="{ data }">
        <div :class="[
          'd-inline-block rounded py-1 px-2',
          `estado-liquidacion ${data.value.replace(' ', '')}`,
        ]">
          {{ $t(`operaciones.liquidado.${data.value}`) }}
        </div>
      </template>
      <template #desgloseComisionTemplate="{ data }">
        <DesgloseRetribucionEconomica
          :importe-total="data.data.ComisionEfectiva"
          :importe-calculado="data.data.Comision"
          :importe-reajuste="data.data.ReajusteComision"
          :motivo-reajuste="data.data.MotivoReajusteComision"
        />
      </template>
      <template #desgloseRetrocomisionTemplate="{ data }">
        <DesgloseRetribucionEconomica
          :importe-total="data.data.RetrocomisionEfectiva"
          :importe-calculado="data.data.Retrocomision"
          :importe-reajuste="data.data.ReajusteRetrocomision"
          :motivo-reajuste="data.data.MotivoReajusteRetrocomision"
          es-retrocomision
        />
      </template>
      <template #exportButtonTemplate>
        <button type="button" @click="onExporting" class="btn btn-sm btn-light d-flex align-items-center gap-2 m-2">
          <i class="fas fa-file-excel exportar-excel"></i>
          <span class="text-primary">{{ $t("shared.ui.buttons.exportToExcel") }}</span>
        </button>
      </template>
    </DxDataGrid>
  </div>
</template>

<script setup lang="ts">
import { useApi } from "@api/integration";
import {
  DxDataGrid,
  DxColumnFixing,
  DxColumn,
  DxLookup,
  DxSummary,
  DxGroupItem,
  DxItem,
  DxToolbar,
  type DxDataGridTypes,
} from "devextreme-vue/data-grid";
import {
  getDefaultGridProperties,
  getLookup,
  getRetrocomisionFilter,
  exportGridToExcel,
} from "@helpers/grid-helper";
import { useModalService } from "@services/modal-service";
import type CustomStore from "devextreme/data/custom_store";
import { onMounted, ref, computed } from "vue";
import OperacionesResumen from "@/components/operaciones-energia/OperacionesEnergiaResumen.vue";
import type DevExpress from "devextreme";
import { useIntl } from "@/.shared/services/i18n/i18nservice";
import { OperacionEnergia } from "@/components/nueva-venta/models/OperacionEnergia";
import type { DXLoadOptions } from "@/.shared/.api";
import DesgloseRetribucionEconomica from "@/.shared/components/operacion/DesgloseRetribucionEconomica.vue";
import { importeRetribucionParaMostrar } from "@helpers/retribucion-economica-helper";

const props = defineProps<{
  templateData: DxDataGridTypes.MasterDetailTemplateData;
}>();

const api = useApi();
const gridSource = ref<CustomStore>();
const grid = ref<InstanceType<typeof DxDataGrid>>();
const modal = useModalService();
const intl = useIntl();

const gridProperties = computed(() => {
  return {
    ...getDefaultGridProperties(),
    remoteOperations: false,
    scrolling: { mode: "standard", rowRenderingMode: "standard"  },
    paging: { enabled: false }
    }
});

onMounted(async () => {

  gridSource.value = api.apiStore({
    key: "IdFactura",
    load: async (p) => {
      const q = p as DXLoadOptions;

      if (q.filter != null && q.filter.length >= 0)
        q.filter = [["IdFactura", "=", props.templateData.key], "and", q.filter];
      else
        q.filter = ["IdFactura", "=", props.templateData.key];

      return await api.callStandardApi("getTramitesEnergiaFacturaForGrid", { showLoading: false }, q)
    }
  });

});

async function verDetalle(data: DevExpress.ui.dxDataGrid.ColumnButtonClickEvent) {
  let title = `${intl.i18n.t("tramites.detalle.titulo")} ${intl.i18n.t(`operaciones.tipo_suministro.${data.row?.data.IdTipoSuministro}`)}
              ${intl.i18n.t("tramites.detalle.titulo_2")} ${data.row?.data.CodigoSecuencial.toString().padStart(10, '0')}`;

  var response = await api.callStandardApi("getOperacionEnergiaDetail", { showLoading: true }, data.row?.data.IdOperacion );
  var model = new OperacionEnergia();
  model.createFromOperacionDetail(response);

  await modal.showModal(OperacionesResumen, { data: model, tramite: data.row?.data.IdTipoSuministro, idTramite: data.row?.data.IdTramite }, { title: title });
}

function formatTotalComision(value: number | null): string {
  if (value == null || value === 0) return "0 €";

  return `${value.toFixed(2).replace(".", ",")} €`;
}

function formatTotalRetrocomision(value: number | null): string {
  if (value == null || value === 0) return "0 €";

  const mostrado = importeRetribucionParaMostrar(value, true) ?? 0;
  return `${mostrado.toFixed(2).replace(".", ",")} €`;
}

function retrocomisionEfectivaFilter(filterValue: unknown, selectedFilterOperation: string) {
  return getRetrocomisionFilter(filterValue, selectedFilterOperation, "RetrocomisionEfectiva");
}

async function onExporting(): Promise<void> {
  const inst = grid.value?.instance;
  if (!inst) {
    return;
  }
  await exportGridToExcel(inst, "TramitesFactura.xlsx", "TramitesFactura", true);
}
</script>

<style lang="scss" scoped>


.lupa {
  color: var(--text-secondary-default);
}

.detalle {
  color: var(--text-primary-default);
  cursor: pointer;
}

.exportar-excel {
  color: $base-success;
}
</style>
